import { type TopicFilterMode } from '@src/components/common/TopicFilterDropdown';
import { type ClipTheme, parseClipThemesJson } from '@src/utility/story';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type TopicMappingStatus,
  useTopicParagraphMapping,
} from './useTopicParagraphMapping';

import type { Story } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type React from 'react';

/**
 * Stable empty set for "topics are live here, nothing matched" (PLAT-11039).
 * Module scope on purpose: a fresh `new Set()` per render would re-fire the
 * DOM-class effect that lists `highlightedParagraphIds` as a dependency.
 */
const EMPTY_PARAGRAPH_IDS: ReadonlySet<string> = new Set();

// Slot 0 matches the existing topic-highlight blue so single-topic selections
// look identical to the pre-multi-color behavior; remaining slots cycle.
const TOPIC_COLOR_PALETTE = [
  '#839fc0', // muted blue (matches --badge-blue-text / single-topic color)
  '#a855f7', // violet
  '#f59e0b', // amber
  '#14b8a6', // teal
  '#6366f1', // indigo
  '#f43f5e', // rose
  '#84cc16', // lime
];

export interface UseTopicFilteringInput {
  enabled: boolean;
  story: Story | null;
  originalElements: TranscriptElement[] | undefined;
  isTranscriptionReady: boolean;
  userIdentity?: { email?: string; id?: string };
}

export interface UseTopicFilteringResult {
  /** Effective gate (input `enabled`, surfaced for parent's conditional render). */
  enabled: boolean;

  // ── Dropdown state ─────────────────────────────────────────────
  topics: ClipTheme[];
  selectedTopics: string[];
  setSelectedTopics: React.Dispatch<React.SetStateAction<string[]>>;
  topicFilterMode: TopicFilterMode;
  setTopicFilterMode: React.Dispatch<React.SetStateAction<TopicFilterMode>>;
  topicMappingStatus: TopicMappingStatus;

  // ── Chip rendering ─────────────────────────────────────────────
  topicColorMap: Map<string, string>;
  /** Matched paragraph count per topic title, for the dropdown's rows. */
  topicMatchCounts: ReadonlyMap<string, number>;
  /**
   * Selected topics matching each paragraph, in selection order — names the
   * rail on hover (the first entry owns the rail's color).
   */
  paragraphTopicTitles: ReadonlyMap<string, string[]>;

  // ── <Transcript> props ────────────────────────────────────────
  visibleParagraphIds: ReadonlySet<string> | undefined;
  highlightedParagraphIds: ReadonlySet<string> | undefined;
  paragraphHighlightColors: ReadonlyMap<string, string> | undefined;
  activeParagraphId: string | undefined;

  // ── Match navigation (prev/next) ──────────────────────────────
  orderedMatchedParagraphIds: string[];
  /** 0-based, already clamped to `orderedMatchedParagraphIds`. */
  activeMatchIndex: number;
  handlePrevMatch: () => void;
  handleNextMatch: () => void;

  /** Attach to the scrollable transcript container for scroll-into-view. */
  transcriptPaneRef: React.RefObject<HTMLDivElement>;
}

/**
 * Topic-filtering state for the Transcript & Speakers tab.
 *
 * Owns:
 *  - parsed `topics` from the story's clipThemesJson
 *  - selected-topic + mode (highlight | show-only) UI state
 *  - the AI-derived paragraph mapping (via `useTopicParagraphMapping`)
 *  - derived sets / colors / ordered list / active-match index needed by
 *    `<Transcript>` and `<MatchNav>`
 *  - auto-scroll of the active match into view
 *
 * Pure React state machinery — no MobX, no IO. The orchestrator does the
 * heavy lifting; this hook composes its result with local UI state.
 */
export function useTopicFiltering(
  input: UseTopicFilteringInput,
): UseTopicFilteringResult {
  const {
    enabled,
    story,
    originalElements,
    isTranscriptionReady,
    userIdentity,
  } = input;

  const topics = useMemo(
    () => (enabled ? parseClipThemesJson(story?.clipThemesJson) : []),
    [enabled, story?.clipThemesJson],
  );

  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [topicFilterMode, setTopicFilterMode] =
    useState<TopicFilterMode>('highlight');

  // Reset selection when the story changes. Content Studio can swap stories
  // without unmounting; without this reset, a stale `selectedTopics` would
  // be looked up against the new story's mapping — in show-only mode the
  // resulting empty match set would hide the entire transcript.
  const storyId = story?.id ?? null;
  useEffect(() => {
    setSelectedTopics([]);
  }, [storyId]);

  // Same-story edge: if the topics list itself changes (story edited elsewhere
  // in the app), drop selections that no longer exist. Preserves a stable
  // array reference when nothing is stale, so this is a no-op for benign
  // topic additions.
  useEffect(() => {
    const titleSet = new Set(topics.map(t => t.title));
    setSelectedTopics(prev =>
      prev.length > 0 && !prev.every(t => titleSet.has(t))
        ? prev.filter(t => titleSet.has(t))
        : prev,
    );
  }, [topics]);

  const { status: topicMappingStatus, mapping: topicMapping } =
    useTopicParagraphMapping({
      enabled,
      story,
      topics,
      originalElements,
      isTranscriptionReady,
      userIdentity,
    });

  const matchedParagraphIds = useMemo(() => {
    if (!topicMapping || selectedTopics.length === 0) return undefined;
    const ids = new Set<string>();
    for (const topic of selectedTopics) {
      const pids = topicMapping.byTopic[topic];
      if (pids) for (const pid of pids) ids.add(pid);
    }
    return ids;
  }, [topicMapping, selectedTopics]);

  // How many paragraphs each topic covers — shown on the dropdown rows
  // (PLAT-11039), so it spans every topic, not just the selected ones.
  const topicMatchCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!topicMapping) return counts;
    for (const topic of topics) {
      const pids = topicMapping.byTopic[topic.title];
      if (pids?.length) counts.set(topic.title, pids.length);
    }
    return counts;
  }, [topicMapping, topics]);

  // Per-topic color assignment. Single-topic selections use the muted
  // badge-blue (matches the existing Topics pill style); multi-topic
  // selections cycle a small palette chosen for hue separation on dark
  // backgrounds.
  const topicColorMap = useMemo(() => {
    const map = new Map<string, string>();
    if (selectedTopics.length === 1) {
      map.set(selectedTopics[0], 'var(--badge-blue-text)');
    } else {
      selectedTopics.forEach((topic, idx) => {
        map.set(topic, TOPIC_COLOR_PALETTE[idx % TOPIC_COLOR_PALETTE.length]);
      });
    }
    return map;
  }, [selectedTopics]);

  // For a paragraph matched by multiple topics, the earliest-selected topic
  // wins (deterministic & matches the dropdown's vertical order).
  const paragraphHighlightColors = useMemo(() => {
    if (!topicMapping || selectedTopics.length === 0) return undefined;
    const colorByPid = new Map<string, string>();
    for (const topic of selectedTopics) {
      const color = topicColorMap.get(topic);
      const pids = topicMapping.byTopic[topic];
      if (!color || !pids) continue;
      for (const pid of pids) {
        if (!colorByPid.has(pid)) colorByPid.set(pid, color);
      }
    }
    return colorByPid;
  }, [topicMapping, selectedTopics, topicColorMap]);

  // Which selected topics each matched paragraph belongs to, in the dropdown's
  // vertical order — so the rail's hover tooltip can name them and the first
  // entry is the topic whose color the rail is painted in.
  const paragraphTopicTitles = useMemo(() => {
    const byPid = new Map<string, string[]>();
    if (!topicMapping) return byPid;
    for (const topic of selectedTopics) {
      const pids = topicMapping.byTopic[topic];
      if (!pids) continue;
      for (const pid of pids) {
        const titles = byPid.get(pid);
        if (titles) titles.push(topic);
        else byPid.set(pid, [topic]);
      }
    }
    return byPid;
  }, [topicMapping, selectedTopics]);

  const visibleParagraphIds =
    topicFilterMode === 'show-only' ? matchedParagraphIds : undefined;
  // PLAT-11039: present — possibly EMPTY — whenever topics are live on this
  // surface. The rail gutter is reserved from this prop's *presence*
  // (`reservesTopicGutter`), so selecting, clearing or switching modes never
  // shifts the transcript text sideways. An empty set paints no rails: every
  // consumer reads it with `.has()`. Surfaces without topics still get
  // `undefined` and keep their original indent.
  //
  // PLAT-11182: matched paragraphs keep their rails in BOTH modes. Show Only
  // used to zero this set, but with several topics selected the per-topic
  // rail colors are what tell you which surviving paragraph belongs to which
  // topic — redundant over the full transcript, load-bearing once only the
  // matches remain.
  const highlightedParagraphIds = !(enabled && topicMappingStatus === 'ready')
    ? undefined
    : (matchedParagraphIds ?? EMPTY_PARAGRAPH_IDS);

  // Ordered list of matched paragraph IDs in transcript document order.
  // Walks originalElements once and records each paragraph's first
  // occurrence, filtering to those in the match set.
  const orderedMatchedParagraphIds = useMemo(() => {
    if (!matchedParagraphIds || matchedParagraphIds.size === 0) return [];
    if (!originalElements) return [];
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const el of originalElements) {
      if (el.type !== 'text') continue;
      const pid = el.paragraph_id;
      if (!pid || seen.has(pid)) continue;
      if (matchedParagraphIds.has(pid)) {
        ordered.push(pid);
        seen.add(pid);
      }
    }
    return ordered;
  }, [matchedParagraphIds, originalElements]);

  const [activeMatchIndex, setActiveMatchIndex] = useState(0);

  useEffect(() => {
    setActiveMatchIndex(0);
  }, [matchedParagraphIds]);

  // Clamped, not raw: the reset-to-0 effect runs after the render in which a
  // new (shorter) match list arrives, so for one render the stored index can
  // point past the end — which would show the nav a "6/3".
  const clampedMatchIndex =
    orderedMatchedParagraphIds.length > 0
      ? Math.min(activeMatchIndex, orderedMatchedParagraphIds.length - 1)
      : 0;

  const activeParagraphId =
    orderedMatchedParagraphIds.length > 0
      ? orderedMatchedParagraphIds[clampedMatchIndex]
      : undefined;

  const transcriptPaneRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!activeParagraphId) return;
    const container = transcriptPaneRef.current;
    if (!container) return;
    const target = container.querySelector(
      `[data-paragraph-id="${CSS.escape(activeParagraphId)}"]`,
    );
    if (target) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [activeParagraphId]);

  const handlePrevMatch = useCallback(() => {
    const total = orderedMatchedParagraphIds.length;
    if (total === 0) return;
    setActiveMatchIndex(i => (i - 1 + total) % total);
  }, [orderedMatchedParagraphIds.length]);

  const handleNextMatch = useCallback(() => {
    const total = orderedMatchedParagraphIds.length;
    if (total === 0) return;
    setActiveMatchIndex(i => (i + 1) % total);
  }, [orderedMatchedParagraphIds.length]);

  return {
    enabled,
    topics,
    selectedTopics,
    setSelectedTopics,
    topicFilterMode,
    setTopicFilterMode,
    topicMappingStatus,
    topicColorMap,
    topicMatchCounts,
    paragraphTopicTitles,
    visibleParagraphIds,
    highlightedParagraphIds,
    paragraphHighlightColors,
    activeParagraphId,
    orderedMatchedParagraphIds,
    activeMatchIndex: clampedMatchIndex,
    handlePrevMatch,
    handleNextMatch,
    transcriptPaneRef,
  };
}
